//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import angular from 'angular';
import { PojoSet } from 'pojo-sets';
import React from 'react';
import { connect } from 'react-redux';
import Redux, { bindActionCreators } from 'redux';
import Cuid from '@models/Cuid';
import { getItemsMetadata } from '@apis/bulkActionsApi';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { IGallery } from '@apis/galleries/galleriesApiModels';
import { Alert } from '@components/atoms/alert/Alert';
import { FullscreenModal } from '@components/FullscreenModal';
import { fullScreenWorkflowActions } from '@galleries/actions/fullScreenWorkflowActions';
import { galleriesAddToGalleriesModalActions } from '@galleries/actions/galleriesAddToGalleriesModalActions';
import { galleryPickerActions } from '@galleries/actions/galleriesPickerActions';
import { requestRightsModalActions } from '@galleries/actions/requestRightsModalActions';
import { rightsOwnedOrPendingModalActions } from '@galleries/actions/rightsOwnedOrPendingModalActions';
import { DarkGradientOverlay } from '@galleries/components/content/dark-gradient-overlay/DarkGradientOverlay';
import GalleriesCheckboxList from '@galleries/components/content/galleries-checkbox-list/GalleriesCheckboxList';
import { InfoOverlay } from '@galleries/components/content/info-overlay/InfoOverlay';
import { MediaGrid } from '@galleries/components/content/media-grid/MediaGrid';
import SelectableImage from '@galleries/components/content/selected-image/SelectableImage';
import { WorkflowBreadcrumbs } from '@galleries/components/content/workflow-breadcrumbs/WorkflowBreadcrumbs';
import {
  BULK_RIGHTS_WORKFLOW_LARGE_MEDIA_WIDTH,
  BULK_RIGHTS_WORKFLOW_SMALL_MEDIA_WIDTH,
  BulkAddtoGalleriesWorkflowTitles,
  GalleriesTrackingIds,
  InstaShopDisabledMessage,
} from '@galleries/constants/galleriesConstants';
import { FULLSCREEN_WORKFLOW_FLAGS } from '@galleries/models/fullScreenWorkflowModel';
import GalleryContainerId from '@galleries/models/GalleryContainerId';
import { galleriesDataActions } from '@galleries/reducers/galleriesDataSlice';
import { canApproveSelectedItemsToInstaShop } from '@galleries/selectors/bulk-rights-selectors/canApproveSelectedItemsToInstaShop';
import { getIsRightsOwnedOnAllMedia } from '@galleries/selectors/bulk-rights-selectors/getIsRightsOwnedOnAllMedia';
import getAreNoGalleriesSelected from '@galleries/selectors/bulk-rights-selectors/getAreNoGalleriesSelected';
import getAreNoCheckboxesSelected from '@galleries/selectors/bulk-rights-selectors/getAreNoCheckboxesSelected';
import { getNumSelectedImages } from '@galleries/selectors/bulk-rights-selectors/getNumSelectedImages';
import { getNumSelectedRightsManagedGalleries } from '@galleries/selectors/bulk-rights-selectors/getNumSelectedRightsManagedGalleries';
import { getSplitRightsOwnedOrPendingMedia } from '@galleries/selectors/bulk-rights-selectors/getSplitRightsOwnedOrPendingMedia';
import { getAllAlphabetizedGalleries } from '@galleries/selectors/getAllAlphabetizedGalleries';
import { getContentItemImageUrl } from '@galleries/utils/getContentItemImageUrl';
import { instagramAccountActions } from '@instagram-account/redux/account/instagramAccountActions';
import { requestRightsActions } from '@legacy-components/rights-management/request-rights-actions';
import { ICuralateStore } from '@models/AppState';
import { ExperienceContainerType } from '@models/ExperienceContainerType';
import styled from 'styled-components';
import { withTracking } from '@utils/withTracking';
import { getShouldDisableItemSyndication } from '@galleries/selectors/bulk-rights-selectors/getShouldDisableItemSyndication';
import { getIsBazaarvoiceSyndicationOnlyClient } from '@galleries/selectors/getIsBazaarvoiceSyndicationOnlyClient';
import PartialCuid from '@models/PartialCuid';
import IItemMetadata from '@models/IItemMetadata';
import { FeatureNames } from '@bvengineering/curalate-features';
import {
  FlatDefaultButton,
  PrimaryButton,
} from '@components/atoms/button/Button';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { UnexpectedErrorAlert } from '@uia/UnexpectedErrorAlert';
import {
  getCuidsWithRightsExpirySet,
  getSyndicationWarningText,
} from '@galleries/utils/bulkAddToGalleriesUtils';
import ClientId from '@models/ClientId';
import { isDefined } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';

const mapStateToProps = (state: ICuralateStore) => {
  const modalState = state.galleries.addToGalleriesModal;
  const { rightsNotOwnedItems, rightsOwnedOrPendingItems } =
    getSplitRightsOwnedOrPendingMedia(state);

  return {
    isOpen: modalState.isOpen,
    materializedItems: modalState.materializedItems,
    initiallySelectedItems: modalState.initiallySelectedItems,
    numSelectedImages: getNumSelectedImages(state),
    selectedGalleries: modalState.gallerySelection,
    currentlySelectedItems: modalState.currentlySelectedItems,
    isLoading: modalState.isLoading,
    isRightsOwnedOnAllImages: getIsRightsOwnedOnAllMedia(state),
    canApproveSelectedContentToInstaShop:
      canApproveSelectedItemsToInstaShop(state),
    hasFanreelModeratePermission: state.session.permissions['fanreel:moderate'],
    rightsRequestStates: modalState.rightsRequestStates,
    rightsNotOwnedItems,
    rightsOwnedOrPendingItems,
    areNoGalleriesSelected: getAreNoGalleriesSelected(state),
    areNoCheckboxesSelected: getAreNoCheckboxesSelected(state),
    numSelectedRightsManagedGalleries:
      getNumSelectedRightsManagedGalleries(state),
    defaultInstagramAccount: state.instagram.account.defaultInstagramAccount,
    areRightsRequestsFetched: state.requestRights.areConfigurationsFetched,
    isBazaarvoiceSyndicationSelected:
      modalState.isBazaarvoiceSyndicationSelected,
    galleries: state.galleries.data.galleries,
    allGalleries: getAllAlphabetizedGalleries(state),
    shouldDisableBazaarvoiceSyndication: getShouldDisableItemSyndication(state),
    isBazaarvoiceSyndicationOnlyClient:
      getIsBazaarvoiceSyndicationOnlyClient(state),
    bazaarvoiceNetworkSyndicationBlockVendorRights:
      !!state.session.features[
        FeatureNames.BazaarvoiceNetworkSyndicationBlockVendorRights
      ],
    rightsManagementPage:
      !!state.session.features[FeatureNames.RightsManagementPage],
    clientId: state.session.client.id,
  };
};

const mapDispatchToProps = (dispatch: Redux.Dispatch) => {
  return {
    actions: {
      addToGalleriesModal: bindActionCreators(
        galleriesAddToGalleriesModalActions,
        dispatch,
      ),
      data: bindActionCreators(galleriesDataActions, dispatch),
      fullScreenWorkflow: bindActionCreators(
        fullScreenWorkflowActions,
        dispatch,
      ),
      galleryPickerActions: bindActionCreators(galleryPickerActions, dispatch),
      requestRightsModal: bindActionCreators(
        requestRightsModalActions,
        dispatch,
      ),
      rightsOwnedModal: bindActionCreators(
        rightsOwnedOrPendingModalActions,
        dispatch,
      ),
      instagramAccount: bindActionCreators(instagramAccountActions, dispatch),
      rightsRequests: bindActionCreators(requestRightsActions, dispatch),
    },
  };
};

type BulkAddToGalleriesModalProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> & {
    mediaMetadata?: Record<Cuid, IItemMetadata>;
    isMediaMetadataLoadingError: boolean;
    isMediaMetadataLoading: boolean;
  };

interface IOwnState {
  showNoSelectedGalleriesWarning: boolean;
  showNoSelectedImagesWarning: boolean;
}

export class BulkAddToGalleriesModal extends React.Component<
  BulkAddToGalleriesModalProps,
  IOwnState
> {
  public state: IOwnState = {
    showNoSelectedGalleriesWarning: false,
    showNoSelectedImagesWarning: false,
  };

  public componentDidMount() {
    const {
      actions: {
        fullScreenWorkflow: { addConditionalFinalToast },
      },
    } = this.props;

    // The ordering in which these actions are dispatched matters, from most to least specific flags
    // fullScreenWorkflowActions will display the first matching toast that it finds
    addConditionalFinalToast(
      {
        title: 'Success',
        message: 'Your items were added to Galleries and have Rights Requested',
      },
      [
        FULLSCREEN_WORKFLOW_FLAGS.ADDED_TO_GALLERIES,
        FULLSCREEN_WORKFLOW_FLAGS.RIGHTS_REQUESTED,
      ],
    );

    addConditionalFinalToast(
      {
        title: 'Success',
        message: 'Your items were added to Galleries.',
      },
      [FULLSCREEN_WORKFLOW_FLAGS.ADDED_TO_GALLERIES],
    );
  }

  public getWorkflowBreadCrumbs(
    isLoading: boolean,
    numSelectedRightsManagedGalleries: number,
    isRightsOwnedOnAllImages: boolean,
  ) {
    if (isRightsOwnedOnAllImages) {
      return null;
    }
    if (isLoading) {
      return (
        <WorkflowBreadcrumbsLoader
          className='loading-element gradient'
          trackingId={GalleriesTrackingIds.ADD_TO_GALLERIES_WORKFLOWS_LOADER}
        />
      );
    } else {
      // If we only have non-rights-managed galleries selected
      //   don't show Request Rights in the list of workflows
      const sectionTitles =
        numSelectedRightsManagedGalleries > 0
          ? [
              BulkAddtoGalleriesWorkflowTitles.ADD_TO_GALLERIES,
              BulkAddtoGalleriesWorkflowTitles.REQUEST_RIGHTS,
            ]
          : [BulkAddtoGalleriesWorkflowTitles.ADD_TO_GALLERIES];

      return (
        <WorkflowBreadcrumbs sectionTitles={sectionTitles} currentIndex={0} />
      );
    }
  }

  public render() {
    const {
      areNoGalleriesSelected,
      areNoCheckboxesSelected,
      currentlySelectedItems,
      isOpen,
      materializedItems,
      selectedGalleries,
      isLoading,
      initiallySelectedItems,
      isRightsOwnedOnAllImages,
      hasFanreelModeratePermission,
      numSelectedRightsManagedGalleries,
      isBazaarvoiceSyndicationSelected,
      actions: {
        addToGalleriesModal: { toggleBazaarvoiceSyndication },
      },
      shouldDisableBazaarvoiceSyndication,
      isBazaarvoiceSyndicationOnlyClient,
      mediaMetadata,
      isMediaMetadataLoading,
      isMediaMetadataLoadingError,
      clientId,
    } = this.props;

    const { showNoSelectedGalleriesWarning, showNoSelectedImagesWarning } =
      this.state;

    const shouldShowLoader = isLoading || isMediaMetadataLoading;
    const numOfInitiallySelectedItems = initiallySelectedItems.length;

    const cuidsWithExpiry = mediaMetadata
      ? getCuidsWithRightsExpirySet(
          mediaMetadata,
          Object.values(currentlySelectedItems)
            .filter((item) => isDefined(item))
            .map((item) =>
              Cuid.build(
                { itemType: item.itemType, id: item.id },
                ClientId.fromString(clientId),
              ),
            ),
        )
      : [];

    const partialCuidsWithExpiry = cuidsWithExpiry.map((cuid) =>
      Cuid.toPartial(cuid),
    );

    const numberOfCurrentlySelected = Object.keys(
      currentlySelectedItems,
    ).length;

    // Only include rights messaging for full clients where some items aren't owned.
    const shouldShowRightsRequestHelperText =
      !isBazaarvoiceSyndicationOnlyClient && !isRightsOwnedOnAllImages;

    return (
      <FullscreenModal
        isActive={true}
        isOpen={isOpen}
        onClose={this.closeModal}
        headerTitle={'Add to Galleries'}
      >
        {!isMediaMetadataLoadingError ? (
          <>
            <div className='cu-mt-4'>
              {this.getWorkflowBreadCrumbs(
                isLoading,
                numSelectedRightsManagedGalleries,
                isRightsOwnedOnAllImages,
              )}
            </div>
            <div className='cu-text-ui-copy-secondary cu-mt-5'>
              {shouldShowLoader ? (
                <ItemWordLoader className='loading-element gradient' />
              ) : (
                `${pluralize(numOfInitiallySelectedItems, 'Item', 'Items', false)}`
              )}
            </div>
            <div className='cu-d-flex cu-mt-4 cu-w-100 cu-justify-content-between'>
              <LeftColumn>
                <MediaGrid>{this.getContent(materializedItems)}</MediaGrid>
              </LeftColumn>
              <RightColumn>
                <div className='cu-text-section cu-pos-relative'>
                  {shouldShowLoader && (
                    <LoadingFiller
                      className='loading-element gradient'
                      percentWidth={50}
                    />
                  )}
                  Add to Galleries
                </div>
                <div className='cu-text-body cu-pos-relative cu-mt-2'>
                  {shouldShowLoader && (
                    <LoadingFiller className='loading-element gradient' />
                  )}
                  Select the galleries you want to add these items to.
                  {shouldShowRightsRequestHelperText &&
                    ` We'll help you request rights next.`}
                  {cuidsWithExpiry.length > 0 && (
                    <StyledAlert type='warning'>
                      {getSyndicationWarningText(
                        numberOfCurrentlySelected,
                        cuidsWithExpiry.length,
                      )}
                    </StyledAlert>
                  )}
                </div>
                <GalleriesCheckboxList
                  areNoGalleriesSelected={areNoGalleriesSelected}
                  areNoCheckboxesSelected={areNoCheckboxesSelected}
                  gallerySelectionState={selectedGalleries}
                  gallerySelected={this.toggleGallerySelected}
                  isLoading={shouldShowLoader}
                  disabledGalleryTypes={this.getDisabledGalleryTypes()}
                  isBazaarvoiceSyndicationSelected={
                    isBazaarvoiceSyndicationSelected
                  }
                  toggleBazaarvoiceSyndication={toggleBazaarvoiceSyndication}
                  shouldDisableBazaarvoiceSyndicationCheckbox={
                    shouldDisableBazaarvoiceSyndication
                  }
                />
                <ButtonsRow>
                  <StyledFlatButton onClick={this.closeModal}>
                    {shouldShowLoader && (
                      <LoadingFiller className='loading-element gradient' />
                    )}
                    Cancel
                  </StyledFlatButton>
                  <SmallTooltip
                    isDisabled={hasFanreelModeratePermission}
                    target={
                      <StyledPrimaryButton
                        isDisabled={!hasFanreelModeratePermission}
                        onClick={() => this.addAndClose(partialCuidsWithExpiry)}
                      >
                        {shouldShowLoader && (
                          <LoadingFiller className='loading-element gradient' />
                        )}
                        Add to Galleries
                      </StyledPrimaryButton>
                    }
                    content={
                      <div>
                        You don't have permission to add to Galleries. Contact a
                        group admin if this is a mistake.
                      </div>
                    }
                  />
                </ButtonsRow>
                {(showNoSelectedGalleriesWarning ||
                  showNoSelectedImagesWarning) && (
                  <WarningContainer className='cu-text-caption'>
                    {showNoSelectedGalleriesWarning && (
                      <div>You must select a gallery before continuing.</div>
                    )}
                    {showNoSelectedImagesWarning && (
                      <div>You must select an image before continuing.</div>
                    )}
                  </WarningContainer>
                )}
              </RightColumn>
            </div>
          </>
        ) : (
          <UnexpectedErrorAlert />
        )}
      </FullscreenModal>
    );
  }

  private closeModal = () => {
    const {
      actions: {
        addToGalleriesModal: { closeModal },
      },
    } = this.props;

    this.setState({
      showNoSelectedGalleriesWarning: false,
      showNoSelectedImagesWarning: false,
    });

    closeModal();
  };

  private selectImage(image: IContentLibraryItem) {
    const {
      actions: {
        addToGalleriesModal: { toggleImage },
      },
      currentlySelectedItems,
    } = this.props;

    const imageWillBeSelected = !currentlySelectedItems[image.id];

    if (imageWillBeSelected) {
      this.setState({ showNoSelectedImagesWarning: false });
    }

    toggleImage(image);
  }

  private getContent(items: IContentLibraryItem[]) {
    const { initiallySelectedItems, currentlySelectedItems, isLoading } =
      this.props;

    if (isLoading) {
      return initiallySelectedItems.map((item) => (
        <div
          key={item.cuid}
          className='loading-element cu-h-100 cu-w-100 gradient'
        />
      ));
    }

    const imgSize =
      items.length === 1
        ? BULK_RIGHTS_WORKFLOW_LARGE_MEDIA_WIDTH
        : BULK_RIGHTS_WORKFLOW_SMALL_MEDIA_WIDTH;

    return items.map((item) => {
      const hoverOverlay = (
        <>
          <DarkGradientOverlay />
          <InfoOverlay contentItem={item} />
        </>
      );
      return (
        <SelectableImage
          key={item.id}
          isSelected={!!currentlySelectedItems[item.id]}
          showSelectableOverlay={true}
          showBorder={true}
          imgSrc={getContentItemImageUrl(item, imgSize)}
          handleClick={() => this.selectImage(item)}
          hoverOverlay={hoverOverlay}
        />
      );
    });
  }

  private toggleGallerySelected = (galleryContainerId: GalleryContainerId) => {
    const {
      actions: {
        addToGalleriesModal: { selectGallery, deselectGallery },
      },
      selectedGalleries,
      galleries: allGalleries,
    } = this.props;

    const gallery = allGalleries.byId[galleryContainerId] as IGallery;
    const reelId = gallery.reelId;

    if (PojoSet.has(selectedGalleries, reelId)) {
      deselectGallery(reelId);
    } else {
      selectGallery(reelId);
      this.setState({ showNoSelectedGalleriesWarning: false });
    }
  };

  private addAndClose = async (cuidsWithExpiry: PartialCuid[]) => {
    const {
      actions: {
        addToGalleriesModal: { addSelectedItemsToGallery },
        requestRightsModal: { openRequestRightsModal },
        rightsOwnedModal,
        instagramAccount: { fetchDefaultInstagramAccount },
        rightsRequests: { requestRightsConfigurations },
      },
      currentlySelectedItems,
      numSelectedImages,
      isRightsOwnedOnAllImages,
      rightsOwnedOrPendingItems,
      rightsNotOwnedItems,
      areNoCheckboxesSelected,
      numSelectedRightsManagedGalleries,
      defaultInstagramAccount,
      areRightsRequestsFetched,
      bazaarvoiceNetworkSyndicationBlockVendorRights,
      rightsManagementPage,
    } = this.props;

    const noImagesSelcted = numSelectedImages === 0;

    if (noImagesSelcted || areNoCheckboxesSelected) {
      this.setState({
        showNoSelectedGalleriesWarning: areNoCheckboxesSelected,
        showNoSelectedImagesWarning: noImagesSelcted,
      });

      return;
    }

    let shouldSkipRightsRequest = false;
    // should skip all rights approved items(including auto approved creator, mark as approved and rights skipped items)
    let approvedItems: IItemMetadata[] = [];

    if (
      !bazaarvoiceNetworkSyndicationBlockVendorRights &&
      rightsManagementPage
    ) {
      const metadata = await getItemsMetadata(
        Object.keys(currentlySelectedItems).map((id: string) =>
          PartialCuid.build(currentlySelectedItems[id]),
        ),
      );

      shouldSkipRightsRequest = metadata.every(
        (itemMetadata: IItemMetadata) =>
          itemMetadata?.rightsPermissions?.creationSource ===
          'RelationshipClientToClient',
      );

      approvedItems = metadata.filter((itemMetadata: IItemMetadata) => {
        const distributionChannels =
          itemMetadata?.rightsPermissions?.distributionChannels ?? [];

        // the existence of any distribution channel means the item has rights
        return distributionChannels.length > 0;
      });
    }

    if (!shouldSkipRightsRequest) {
      const selectedRightsOwnedOrPendingItems =
        rightsOwnedOrPendingItems.filter(
          (rightsOwnedItem) => currentlySelectedItems[rightsOwnedItem.id],
        );

      const currentlySelectedRightsNotOwnedItems = rightsNotOwnedItems.filter(
        (rightsNotOwnedItem) => currentlySelectedItems[rightsNotOwnedItem.id],
      );

      const selectedApprovedItems = approvedItems
        .map((item) => {
          const pid = Cuid.parse(item.rootCuid);
          return currentlySelectedItems[pid.itemId];
        })
        .filter((item) => item !== undefined) as IContentLibraryItem[];

      const totalRightsOwnedItems = [
        ...selectedRightsOwnedOrPendingItems,
        ...selectedApprovedItems,
      ];

      // removes duplicates
      const selectedRightsOwnedItems = totalRightsOwnedItems.filter(
        (item, index, array) => array.indexOf(item) === index,
      );

      const selectedRightsNotOwnedItems =
        currentlySelectedRightsNotOwnedItems.filter(
          (el) => !selectedRightsOwnedItems.includes(el),
        );

      if (selectedRightsOwnedItems.length > 0) {
        rightsOwnedModal.openModal(
          selectedRightsOwnedItems,
          Object.keys(currentlySelectedItems).length,
        );
      }

      // We only need to request rights if we're approving content to a rights-managed gallery.
      const anyRightsManagedGalleriesSelected =
        numSelectedRightsManagedGalleries > 0;

      if (!isRightsOwnedOnAllImages && anyRightsManagedGalleriesSelected) {
        const loadingPromises = [];

        if (!defaultInstagramAccount) {
          loadingPromises.push(fetchDefaultInstagramAccount());
        }
        if (!areRightsRequestsFetched) {
          loadingPromises.push(requestRightsConfigurations());
        }

        await Promise.all(loadingPromises);

        openRequestRightsModal(selectedRightsNotOwnedItems);
      }
    }

    await addSelectedItemsToGallery(cuidsWithExpiry);

    this.closeModal();
  };

  private getDisabledGalleryTypes = () => {
    const { canApproveSelectedContentToInstaShop } = this.props;

    if (!canApproveSelectedContentToInstaShop) {
      return {
        [ExperienceContainerType.INSTASHOP]: InstaShopDisabledMessage,
      };
    }

    return {};
  };
}

const WarningContainer = styled.div`
  && {
    color: ${({ theme }) => theme.colors.red8};
  }

  display: flex;
  align-items: flex-end;
  flex-direction: column;
`;

const RightColumn = styled.div`
  width: 40%;
`;

const LeftColumn = styled.div`
  margin-right: ${({ theme }) => theme.spacings[4]};
  width: 60%;
`;

const ButtonsRow = styled.div`
  margin-top: ${({ theme }) => theme.spacings[5]};
  display: flex;
  justify-content: flex-end;
`;

const StyledFlatButton = styled(FlatDefaultButton)`
  margin-left: ${({ theme }) => theme.spacings[2]};
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  margin-left: ${({ theme }) => theme.spacings[2]};
`;

const ItemWordLoader = styled.div`
  width: 100px;
  height: 20px;
`;

const WorkflowBreadcrumbsLoader = withTracking(styled.div`
  height: 30px;
  width: 200px;
`);

export const StyledAlert = styled(Alert)`
  margin: ${({ theme }) => theme.spacings[2]} 0;
`;

interface ILoadableFiller {
  percentWidth?: number;
}

const LoadingFiller = styled.div<ILoadableFiller>`
  position: absolute;
  height 100%;
  width: ${({ percentWidth }) => (percentWidth ? `${percentWidth}%` : '100%')};
  top: 0;
  left: 0;
`;

const ConnectedBulkAddToGalleries = connect(
  mapStateToProps,
  mapDispatchToProps,
)(BulkAddToGalleriesModal);

export default ConnectedBulkAddToGalleries;
